import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import { getMasterTrades } from 'app/state/ducks/master-trades/actions'
import { getSubTrades } from 'app/state/ducks/sub-trades/actions'
import { getTradedFacilities } from 'app/state/ducks/traded-facilities/actions'
import { getFacilityAmounts, getLockedFacilities } from 'app/state/ducks/facilities/actions'
import { getDocumentByContract } from 'app/state/ducks/documents/actions'
import { ContractType } from 'app/models/common-types'
import {
  getFMMetadatas,
  getSignableDocuments,
} from 'app/state/ducks/signable-document/actions'
import {
  bumpLockedFMMetadatasGeneration,
  currentLockedFMMetadatasGeneration,
} from 'app/state/ducks/signable-document/thunks'
import { loadDeliveries } from 'app/state/ducks/trade-document-delivery/actions'
import { setTradeDetailLookups, setTradeOverview } from './actions'
import { loadTradeNotes } from 'app/state/ducks/trade-notes/actions'

// LATEST-WINS: only the newest response lands, so Trade Search switching
// trades without remounting the page drops a slow response for the trade it
// has left. A plain load SHARES a request already in flight for the same
// trade (React's strict mode runs a mount effect twice in development); a
// refetch after a write passes `fresh` — it must never reuse a request that
// started before the write.
let overviewSequence = 0
type InFlight = { sequence: number; promise: Promise<unknown> }
const inFlight = new Map<string, InFlight>()

/**
 * The request in flight for this trade, while no later read of the same
 * sequence has started. One that a later read has overtaken would resolve
 * without landing (its response is dropped as stale), so sharing it would
 * leave the caller with nothing in the store.
 */
const current = (
  requests: Map<string, InFlight>,
  masterTradeId: string,
  latest: () => number,
) => {
  const entry = requests.get(masterTradeId)
  return entry && entry.sequence === latest() ? entry.promise : undefined
}

/** Starts `run` (which takes the next sequence) and records it as in flight. */
const track = (
  requests: Map<string, InFlight>,
  masterTradeId: string,
  run: () => Promise<unknown>,
  latest: () => number,
) => {
  const request: Promise<unknown> = run().finally(() => {
    if (requests.get(masterTradeId)?.promise === request) requests.delete(masterTradeId)
  })
  // run() took its sequence synchronously, before its first await
  requests.set(masterTradeId, { sequence: latest(), promise: request })
  return request
}
const latestOverview = () => overviewSequence

/**
 * The Overview tab's one read (ADR_LOS-030): `GET /api/trade-detail/:id/overview`
 * — one request, one PQS statement, instead of the seventeen tenant-wide
 * lists the page shell loaded for every tab.
 *
 * The master trade goes to the master-trade store, in whichever state it is
 * (a locked trade carries its status and reason on the item): the header,
 * the actions menu and the edit forms read it from the store, and an edit
 * updates it in place. The rest — entities, deal, the Traded Assets rows,
 * close date, the cancel gate and the header's two warnings, all computed on
 * the server — goes to the page's slice.
 */
export const loadTradeOverview =
  (masterTradeId: string, options: { fresh?: boolean } = {}) =>
  (dispatch: Dispatch) => {
    const shared = current(inFlight, masterTradeId, latestOverview)
    if (shared && !options.fresh) return shared
    const run = async () => {
      const sequence = ++overviewSequence
      const apiServerUrl = config.backendUrl
      try {
        const response: any = await client.get(
          `${apiServerUrl}/api/trade-detail/${encodeURIComponent(masterTradeId)}/overview`,
        )
        if (sequence !== overviewSequence) return { success: false, stale: true }
        const { masterTrade, ...view } = response.data
        dispatch(getMasterTrades(masterTrade ? [masterTrade] : []))
        dispatch(setTradeOverview({ masterTradeId, ...view }))
        return { success: true }
      } catch (e: any) {
        // the store keeps what it had; the API client logs the request
        return {
          success: false,
          payload: e?.response?.data?.error || 'Failed to load trade overview',
        }
      }
    }
    return track(inFlight, masterTradeId, run, latestOverview)
  }

// The Activity tab's read shares the Overview's sequence: both write the page's overview slice
// (and the master trade), so only the newer of the two lands. Its own in-flight map, as the
// Sub-trade and Documents reads share one sequence with a map each.
const activityInFlight = new Map<string, InFlight>()

/**
 * The Activity Stream tab's one read (LOS-7095): `GET /api/trade-detail/:id/activity` — one
 * request, one PQS statement — instead of the whole Sub-trade read plus a separate notes
 * request once the tab mounted.
 *
 * The master trade goes to the master-trade store, as on the Overview (the header, the
 * actions menu and Delete Draft read it there). The header's warnings and the composer's sub
 * trades go to the page's overview slice; the notes go to the trade-notes store, whose merge
 * keeps a fresher note than a stale read (LOS-7094).
 *
 * Latest-wins and in-flight sharing as `loadTradeOverview`; a refetch after a write, or the
 * stream's Refresh button, passes `fresh`.
 */
export const loadTradeActivity =
  (masterTradeId: string, options: { fresh?: boolean } = {}) =>
  (dispatch: Dispatch) => {
    const shared = current(activityInFlight, masterTradeId, latestOverview)
    if (shared && !options.fresh) return shared
    const run = async () => {
      const sequence = ++overviewSequence
      const apiServerUrl = config.backendUrl
      try {
        const response: any = await client.get(
          `${apiServerUrl}/api/trade-detail/${encodeURIComponent(masterTradeId)}/activity`,
        )
        if (sequence !== overviewSequence) return { success: false, stale: true }
        const { masterTrade, items, ...view } = response.data
        dispatch(getMasterTrades(masterTrade ? [masterTrade] : []))
        dispatch(setTradeOverview({ masterTradeId, ...view }))
        dispatch(loadTradeNotes(items ?? []))
        return { success: true }
      } catch (e: any) {
        // An overtaken read that fails is stale too, not a failure: the newer read is the
        // one that counts, so the stream's Refresh must not toast over data that loaded.
        if (sequence !== overviewSequence) return { success: false, stale: true }
        // the stores keep what they had; the API client logs the request
        return {
          success: false,
          payload: e?.response?.data?.error || 'Failed to load trade activity',
        }
      }
    }
    return track(activityInFlight, masterTradeId, run, latestOverview)
  }

/**
 * The Sub-trade read's records into the stores the tabs read, its lookups
 * into the page's slice — shared by the Sub-trade and Documents reads, which
 * return the same records.
 */
const applyTradeDetail = (dispatch: Dispatch, masterTradeId: string, detail: any) => {
  // The slice first: the header reads its warnings from it the moment
  // the master trade lands in its store.
  dispatch(
    setTradeDetailLookups({
      masterTradeId,
      deals: detail.deals ?? [],
      facilities: detail.facilities ?? [],
      entities: detail.entities ?? [],
      entityTypes: detail.entityTypes ?? [],
      wireInstructions: detail.wireInstructions ?? [],
      fullySignedAaMetadata: detail.fullySignedAaMetadata ?? [],
      aaMetadata: detail.aaMetadata ?? [],
      header: detail.header ?? null,
    }),
  )
  dispatch(getMasterTrades(detail.masterTrades ?? []))
  dispatch(getSubTrades(detail.subTrades ?? []))
  dispatch(getTradedFacilities(detail.tradedFacilities ?? []))
  dispatch(getLockedFacilities(detail.lockedFacilities ?? []))
  dispatch(getFacilityAmounts(detail.facilityAmounts ?? []))
  dispatch(
    getDocumentByContract({
      data: detail.attachments ?? [],
      type: ContractType.MasterTrade,
    }),
  )
}

// ONE sequence for the Sub-trade and Documents reads: both fill the same
// stores, so only the newest of either lands. A request in flight is shared
// only while it is still the newest (`current`): e.g. Documents → Sub-trade
// → Documents before the first Documents read returns must not reuse it, or
// its documents never arrive.
let detailSequence = 0
const latestDetail = () => detailSequence
const detailInFlight = new Map<string, InFlight>()

/**
 * The Sub-trade tab's one read (ADR_LOS-030 step 2): `GET /api/trade-detail/:id`
 * — one request, one PQS statement, instead of the seventeen tenant-wide
 * lists, the fully-signed-AA ledger call and the attachments call.
 *
 * The trade's own records go into the stores the tab, its expand panel and
 * its editors already read (master trade, sub trades, traded facilities, the
 * deal's locked facilities, facility amounts, the sub trades' attachments) —
 * each item in the shape those stores held, so every reader and in-place
 * edit keeps working. The deal, its facilities, the referenced entities,
 * their types and wire instructions go to the page's own slice (never the
 * shared stores, which other pages read as the whole tenant), with the fully
 * signed AA metadata and the header's warnings.
 *
 * Latest-wins and in-flight sharing as `loadTradeOverview`; a refetch after
 * a write passes `fresh`.
 */
export const loadTradeDetail =
  (masterTradeId: string, options: { fresh?: boolean } = {}) =>
  (dispatch: Dispatch) => {
    const shared = current(detailInFlight, masterTradeId, latestDetail)
    if (shared && !options.fresh) return shared
    const run = async () => {
      const sequence = ++detailSequence
      const apiServerUrl = config.backendUrl
      try {
        const response: any = await client.get(
          `${apiServerUrl}/api/trade-detail/${encodeURIComponent(masterTradeId)}`,
        )
        if (sequence !== detailSequence) return { success: false, stale: true }
        applyTradeDetail(dispatch, masterTradeId, response.data)
        return { success: true }
      } catch (e: any) {
        // the stores keep what they had; the API client logs the request
        return {
          success: false,
          payload: e?.response?.data?.error || 'Failed to load trade',
        }
      }
    }
    return track(detailInFlight, masterTradeId, run, latestDetail)
  }

const documentsInFlight = new Map<string, InFlight>()

/**
 * The Documents tab's one read (ADR_LOS-030 step 3):
 * `GET /api/trade-detail/:id/documents` — one request, one PQS statement:
 * the Sub-trade read's records and lookups (the tab's rows, expand panel and
 * bulk actions read the same) plus the tab's own sets, instead of the
 * seventeen tenant-wide lists, every signable document and funding memo in
 * the tenant, the attachments call, two AA metadata calls and the
 * deliveries list (whose staleness check read the ledger per sub trade and
 * per facility).
 *
 * The records and lookups land as `loadTradeDetail` puts them. The tab's
 * sets go into the stores it reads: signable documents (this trade's), the
 * locked funding-memo metadata (under the same generation guard as
 * `loadAllLockedFMMetadatas`: a fresh FM reload since the request started
 * wins) and deliveries (upserted, as every delivery write does).
 *
 * Latest-wins (shared with `loadTradeDetail`) and in-flight sharing as
 * `loadTradeOverview`; a refetch after a write passes `fresh`.
 */
export const loadTradeDocuments =
  (masterTradeId: string, options: { fresh?: boolean } = {}) =>
  (dispatch: Dispatch) => {
    const shared = current(documentsInFlight, masterTradeId, latestDetail)
    if (shared && !options.fresh) return shared
    const run = async () => {
      const sequence = ++detailSequence
      const fmGeneration = options.fresh
        ? bumpLockedFMMetadatasGeneration()
        : currentLockedFMMetadatasGeneration()
      const apiServerUrl = config.backendUrl
      try {
        const response: any = await client.get(
          `${apiServerUrl}/api/trade-detail/${encodeURIComponent(masterTradeId)}/documents`,
        )
        if (sequence !== detailSequence) return { success: false, stale: true }
        const documents = response.data
        applyTradeDetail(dispatch, masterTradeId, documents)
        dispatch(getSignableDocuments(documents.signableDocuments ?? []))
        if (fmGeneration === currentLockedFMMetadatasGeneration()) {
          dispatch(getFMMetadatas(documents.fmMetadatas ?? []))
        }
        dispatch(loadDeliveries(documents.deliveries ?? []))
        return { success: true }
      } catch (e: any) {
        // the stores keep what they had; the API client logs the request
        return {
          success: false,
          payload: e?.response?.data?.error || 'Failed to load trade documents',
        }
      }
    }
    return track(documentsInFlight, masterTradeId, run, latestDetail)
  }
